import React, { createContext, useContext, useState, useEffect } from 'react';

export type InviteTheme = 'dark' | 'light';

interface InviteThemeContextType {
    theme: InviteTheme;
    toggleTheme: () => void;
    setTheme: (theme: InviteTheme) => void;
}

const InviteThemeContext = createContext<InviteThemeContextType>({
    theme: 'dark',
    toggleTheme: () => {},
    setTheme: () => {},
});

export const InviteThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<InviteTheme>(() => {
        try {
            const saved = localStorage.getItem('shadigram_invite_theme');
            if (saved === 'light' || saved === 'dark') {
                return saved;
            }
        } catch (e) {
            // ignore localStorage errors
        }
        return 'dark'; // default to modern dark obsidian theme
    });

    const setTheme = (newTheme: InviteTheme) => {
        setThemeState(newTheme);
        try {
            localStorage.setItem('shadigram_invite_theme', newTheme);
        } catch (e) {
            // ignore
        }
    };

    const toggleTheme = () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    };

    useEffect(() => {
        if (theme === 'light') {
            document.body.classList.add('invite-light-theme-active');
        } else {
            document.body.classList.remove('invite-light-theme-active');
        }
    }, [theme]);

    return (
        <InviteThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </InviteThemeContext.Provider>
    );
};

export const useInviteTheme = () => useContext(InviteThemeContext);
export default InviteThemeContext;
